/**
 * Правила валидации формы контакта.
 * Ключи сообщений для i18n: index.contactForm.errors.*
 */
export const CONTACT_FORM_ERROR_KEYS = {
  required: 'index.contactForm.errors.required',
  fullNameLength: 'index.contactForm.errors.fullNameLength',
  phone: 'index.contactForm.errors.phone',
  email: 'index.contactForm.errors.email',
  messageMin: 'index.contactForm.errors.messageMin',
  privacyRequired: 'index.contactForm.errors.privacyRequired',
} as const

type Validator = (value: unknown) => true | string

const required: Validator = (value) => {
  if (value === undefined || value === null) {
    return CONTACT_FORM_ERROR_KEYS.required
  }
  const s = String(value).trim()
  if (s.length === 0) {
    return CONTACT_FORM_ERROR_KEYS.required
  }
  return true
}

const fullNameLength: Validator = (value) => {
  if (!value) return true
  const length = String(value).trim().length
  if (length < 2 || length > 100) {
    return CONTACT_FORM_ERROR_KEYS.fullNameLength
  }
  return true
}

const phoneFormat: Validator = (value) => {
  if (!value || String(value).trim().length === 0) return true
  // Разрешаем цифры, пробелы, +, -, (, )
  const phoneRegex = /^[\d\s+\-()]+$/
  const cleaned = String(value).replace(/[\s\-()]/g, '')
  // Минимум 10 цифр, максимум 15
  if (cleaned.length < 10 || cleaned.length > 15) {
    return CONTACT_FORM_ERROR_KEYS.phone
  }
  return phoneRegex.test(String(value)) ? true : CONTACT_FORM_ERROR_KEYS.phone
}

const emailFormat: Validator = (value) => {
  if (!value || String(value).trim().length === 0) return true
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(String(value)) ? true : CONTACT_FORM_ERROR_KEYS.email
}

const messageMinLength = (min: number): Validator => (value) => {
  if (!value) return true
  const length = String(value).trim().length
  if (length < min) {
    return CONTACT_FORM_ERROR_KEYS.messageMin
  }
  return true
}

/** Правило: fullName — обязательно, 2–100 символов */
export function validateFullName(value: unknown): true | string {
  const r = required(value)
  if (r !== true) return r
  return fullNameLength(value)
}

/** Правило: phone — обязательно, формат телефона */
export function validatePhone(value: unknown): true | string {
  const r = required(value)
  if (r !== true) return r
  return phoneFormat(value)
}

/** Правило: email — обязательно, формат email */
export function validateEmail(value: unknown): true | string {
  const r = required(value)
  if (r !== true) return r
  return emailFormat(value)
}

/** Правило: email — необязательно, если указано — проверка формата */
export function validateEmailOptional(value: unknown): true | string {
  return emailFormat(value)
}

/** Правило: message — необязательно, если указано — минимум 10 символов */
export function validateMessageOptional(value: unknown): true | string {
  if (!value || String(value).trim().length === 0) return true
  return messageMinLength(10)(value)
}

/** Правило: region (область) — обязательно при включённом showRegion */
export function validateRegionRequired(value: unknown): true | string {
  return required(value)
}

/** Правило: чекбокс политики конфиденциальности */
export function validatePrivacyAccepted(value: unknown): true | string {
  if (value === true) return true
  return CONTACT_FORM_ERROR_KEYS.privacyRequired
}

/**
 * Набор правил валидации полей формы контакта.
 * Используется в useContactForm для построения схемы валидации.
 */
export const contactFormRules = {
  fullName: validateFullName,
  phone: validatePhone,
  phoneOptional: phoneFormat,
  email: validateEmail,
  emailOptional: validateEmailOptional,
  messageOptional: validateMessageOptional,
  regionRequired: validateRegionRequired,
  privacyAccepted: validatePrivacyAccepted,
} as const
